<template>
  <a-card size="small" class="user-profile-firebase">
    <a-form v-if="firebaseUser" layout="vertical">
      <a-form-item :label="t('global.email')">
        <a-input :value="email" readonly placeholder="-" />
      </a-form-item>

      <PhoneNumberInput :value="phoneNumber" readonly :show-validation="false" :has-feedback="false" />

      <a-form-item label="Sign-in providers">
        <div class="provider-tags">
          <div
            v-for="provider in socialProviderTags"
            :key="provider.id"
            class="provider-tag"
            :class="[provider.enabled ? 'is-enabled' : 'is-disabled', `provider-tag--${provider.kind}`]"
            :role="provider.clickable ? 'button' : null"
            :tabindex="provider.clickable ? 0 : null"
            @click="provider.clickable ? handleProviderClick(provider) : null"
            @keydown.enter.prevent="provider.clickable ? handleProviderClick(provider) : null"
            @keydown.space.prevent="provider.clickable ? handleProviderClick(provider) : null">
            <span class="provider-tag__icon" aria-hidden="true">
              <svg
                v-if="provider.kind === 'google'"
                class="provider-tag__google-icon"
                viewBox="0 0 48 48"
                aria-hidden="true"
                focusable="false">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.62 17.74 9.5 24 9.5z" />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6.0c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-6.0c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.57-4.12-13.47-9.7l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                <path fill="none" d="M0 0h48v48H0z" />
              </svg>
              <i v-else-if="provider.kind === 'facebook'" class="fa-brands fa-facebook-f" aria-hidden="true"></i>
              <i v-else-if="provider.kind === 'apple'" class="fa-brands fa-apple" aria-hidden="true"></i>
              <i v-else-if="provider.kind === 'password'" class="fa-solid fa-envelope" aria-hidden="true"></i>
            </span>
            <span class="provider-tag__label">{{ provider.label }}</span>
            <span v-if="provider.linking" class="provider-tag__action">Connecting...</span>
            <span v-else-if="provider.actionLabel" class="provider-tag__action">{{ provider.actionLabel }}</span>
            <a-popconfirm
              v-if="provider.canUnlink"
              :title="`Disconnect ${provider.label}?`"
              ok-text="Disconnect"
              cancel-text="Cancel"
              @confirm="handleUnlinkProvider(provider)">
              <a-button
                type="text"
                size="small"
                class="provider-tag__disconnect"
                :disabled="provider.unlinking"
                @click.stop>
                ✕
              </a-button>
            </a-popconfirm>
            <span class="provider-tag__status" aria-hidden="true"></span>
          </div>
        </div>
      </a-form-item>
    </a-form>

    <div v-else class="user-profile-firebase__empty">-</div>
  </a-card>

  <a-modal
    v-model:open="showPasswordModal"
    :title="passwordModalTitle"
    :destroy-on-close="true"
    :footer="null"
    :width="420"
    centered
    @cancel="closePasswordModal">
    <PasswordChangeView
      :has-password-provider="hasPasswordProvider"
      @close="closePasswordModal"
      @updated="handlePasswordUpdated" />
  </a-modal>
</template>

<script setup>
import { message } from "ant-design-vue"
import { getAuth, unlink } from "firebase/auth"
import { computed, onMounted, onUnmounted, ref } from "vue"

import PasswordChangeView from "@/components/Auth/PasswordChangeView.vue"
import firebaseSignupService from "@/components/Auth/FirebaseSignupService"
import PhoneNumberInput from "@/components/Address/PhoneNumberInput.vue"
import { i18n } from "@/i18n"
import reportError from "@/utils/reportError"

const t = (...args) => i18n.global.t(...args)

const firebaseUser = ref(null)
const firebaseUserRevision = ref(0)

const setFirebaseUser = (user) => {
  firebaseUser.value = user
  // Firebase User is not reactive; bump a revision counter so computed properties can re-evaluate.
  firebaseUserRevision.value += 1
}

const reloadFirebaseUser = async () => {
  const user = getAuth().currentUser
  if (!user) {
    setFirebaseUser(null)
    return
  }
  try {
    if (user?.reload) {
      await user.reload()
    }
  } catch (error) {
    reportError(error, { context: "UserProfileFirebase.reloadFirebaseUser" })
  }
  setFirebaseUser(getAuth().currentUser)
}

let unsubscribeAuthListener = null
onMounted(() => {
  const auth = getAuth()
  setFirebaseUser(auth.currentUser)
  unsubscribeAuthListener = auth.onAuthStateChanged((user) => {
    setFirebaseUser(user)
  })
})
onUnmounted(() => {
  if (typeof unsubscribeAuthListener === "function") {
    unsubscribeAuthListener()
  }
  unsubscribeAuthListener = null
})

const email = computed(() => {
  firebaseUserRevision.value
  const user = firebaseUser.value
  if (!user) return ""
  if (user.email) return user.email
  return user.providerData?.find((provider) => provider?.email)?.email || ""
})

const phoneNumber = computed(() => {
  firebaseUserRevision.value
  const user = firebaseUser.value
  if (!user) return ""
  if (user.phoneNumber) return user.phoneNumber
  return user.providerData?.find((provider) => provider?.phoneNumber)?.phoneNumber || ""
})

const providerIds = computed(() => {
  firebaseUserRevision.value
  const user = firebaseUser.value
  const ids = new Set()
  ;(user?.providerData || []).forEach((provider) => {
    if (provider?.providerId) {
      ids.add(provider.providerId)
    }
  })
  return Array.from(ids)
})

const hasPasswordProvider = computed(() => providerIds.value.includes("password"))
const passwordModalTitle = computed(() =>
  hasPasswordProvider.value ? t("account.password.change") : t("account.password.set")
)

const socialProviders = computed(() => [
  { id: "google.com", label: "Google", kind: "google" },
  { id: "facebook.com", label: "Facebook", kind: "facebook" },
  { id: "apple.com", label: "Apple", kind: "apple" },
  { id: "password", label: t("account.password.0") || "Password", kind: "password" }
])

const linkingProvider = ref("")
const unlinkingProvider = ref("")
const showPasswordModal = ref(false)

const socialProviderTags = computed(() => {
  const enabledIds = new Set(providerIds.value)
  const enabledCount = enabledIds.size
  const isLinking = !!linkingProvider.value
  return socialProviders.value.map((provider) => {
    const isPassword = provider.kind === "password"
    const enabled = enabledIds.has(provider.id)
    const linkable = !isPassword && !isLinking && firebaseSignupService.canLinkSocialProvider(provider.kind, firebaseUser.value)
    const clickable = (!linkingProvider.value && !unlinkingProvider.value) && (isPassword || linkable)
    const actionLabel = isPassword
      ? enabled
        ? t("account.password.change")
        : t("account.password.set")
      : linkable
        ? "Connect"
        : ""
    return {
    ...provider,
    enabled,
    linkable,
    clickable,
    actionLabel,
    linking: linkingProvider.value === provider.kind,
    unlinking: unlinkingProvider.value === provider.id,
    canUnlink:
      enabledIds.has(provider.id) && enabledCount > 1 && !linkingProvider.value && unlinkingProvider.value !== provider.id
    }
  })
})

const openPasswordModal = () => {
  showPasswordModal.value = true
}

const closePasswordModal = () => {
  showPasswordModal.value = false
}

const handlePasswordUpdated = () => {
  setFirebaseUser(getAuth().currentUser)
  closePasswordModal()
}

const handleProviderClick = async (provider) => {
  if (!provider?.clickable) return
  if (provider.kind === "password") {
    openPasswordModal()
    return
  }
  if (provider.linkable) {
    await handleLinkProvider(provider)
  }
}

const handleLinkProvider = async (provider) => {
  if (!provider?.linkable) return
  if (linkingProvider.value) return

  linkingProvider.value = provider.kind
  try {
    await firebaseSignupService.linkSocialProviderPopup(provider.kind)
    await reloadFirebaseUser()
    message.success(`${provider.label} connected`, 1.5)
  } catch (error) {
    if (String(error?.code || "") !== "auth/popup-closed-by-user") {
      reportError(error, { context: "UserProfileFirebase.linkProvider", provider: provider.kind })
      message.error("Could not connect provider.")
    }
  } finally {
    linkingProvider.value = ""
  }
}

const handleUnlinkProvider = async (provider) => {
  if (!provider?.enabled) return
  if (unlinkingProvider.value) return

  unlinkingProvider.value = provider.id
  try {
    const user = getAuth().currentUser
    if (!user) {
      message.error("No user session available.")
      return
    }
    await unlink(user, provider.id)
    await reloadFirebaseUser()
    message.success(`${provider.label} disconnected`, 1.5)
  } catch (error) {
    reportError(error, { context: "UserProfileFirebase.unlinkProvider", provider: provider.id })
    message.error("Could not disconnect provider.")
  } finally {
    unlinkingProvider.value = ""
  }
}
</script>

<style scoped lang="scss">
.user-profile-firebase__empty {
  color: #64748b;
}

.provider-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.provider-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
}

.provider-tag[role="button"] {
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.provider-tag[role="button"]:hover {
  border-color: #94a3b8;
  background: #e2e8f0;
  color: #0f172a;
}

.provider-tag[role="button"].provider-tag--google:hover {
  border-color: #1a73e8;
  background: #e8f0fe;
  color: #0f172a;
}

.provider-tag[role="button"].provider-tag--facebook:hover {
  border-color: #1877f2;
  background: #e7f1ff;
  color: #0f172a;
}

.provider-tag[role="button"].provider-tag--apple:hover {
  border-color: #111827;
  background: #111827;
  color: #fff;
}

.provider-tag[role="button"].provider-tag--apple:hover .provider-tag__action {
  color: #fff;
  background: #374151;
}

.provider-tag__action {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  background: #e2e8f0;
  padding: 2px 6px;
  border-radius: 999px;
}

.provider-tag__disconnect {
  font-size: 12px;
  line-height: 1;
  height: 20px;
  padding: 0 6px;
  color: #94a3b8;
}

.provider-tag__disconnect:hover {
  color: #ef4444;
  background: transparent;
}

.provider-tag__icon {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.provider-tag__google-icon {
  width: 16px;
  height: 16px;
  display: block;
}

.provider-tag__status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
}

.provider-tag.is-enabled {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #0f172a;
}

.provider-tag.is-disabled {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #94a3b8;
}

.provider-tag.is-disabled .provider-tag__status {
  background: #cbd5e1;
}

.provider-tag.is-disabled .provider-tag__icon {
  filter: grayscale(1);
  opacity: 0.65;
}

.provider-tag.is-disabled .provider-tag__action {
  color: #64748b;
  background: #e2e8f0;
}
</style>
